import { useMemo } from "react";
import { usePaginatedQuery } from "../../Hooks/use-paginated-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, useState} from "../shared/index";
import { PageLayout } from "../../Components/PageLayout";
import { DataTableColumn } from "../../Components/ui/table/types";

import { StatusBadge } from "../../Components/ui/statusBadge";
import { DataTable } from "../../Components/ui/table";
import { FiltersWrapper } from "../../Components/ui/table/filters";
import { InputFilter } from "../../Components/ui/table/filters/input";
import { SelectFilter } from "../../Components/ui/table/filters/select/select-filter";
import { CursorPagination } from "../../Components/ui/table/pagination";
import { CreateLocation } from "./create-location";

type LocationRow = {
  id: number;
  loc_name: string;
  loc_street: string;
  loc_city: string;
  state: string;
  loc_country: string;
  is_active: boolean;
};

const columns: DataTableColumn<LocationRow>[] = [
  {
    header: "Name",
    labelKey: "name",
    field: "loc_name",
    sortable: true,
  },
  {
    header: "Address",
    labelKey: "address",
    field: "loc_street",
    sortable: true,
  },
  {
    header: "City",
    labelKey: "city",
    field: "loc_city",
    sortable: true,
  },
  {
    header: "State",
    labelKey: "state",
    field: "state",
    sortable: true,
  },
  {
    header: "Country",
    labelKey: "country",
    field: "loc_country",
    sortable: true,
  },
  {
    header: "Status",
    labelKey: "status",
    cell: (row: LocationRow) => {
      const isActive = row.is_active;
      return (
        <StatusBadge
          variant={isActive ? "positive" : "negative"}
          labelKey={isActive ? "active" : "inactive"}
        />
      );
    },
  },
];

const initialUrl = "/assets/locations/v2/";

const LocationsRoute = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [nameFilter, setNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("true");

  const [createLocationModalOpen, setCreateLocationModalOpen] = useState(false);

  const params = useMemo(
    () => ({
      is_active: statusFilter === "true",
      ...(nameFilter.trim() ? { name: nameFilter.trim() } : {}),
    }),
    [nameFilter, statusFilter],
  );

  const results = usePaginatedQuery<LocationRow>({
    initialUrl: initialUrl,
    params: params,
  });

  return (
    <PageLayout title={"Locations"} header="locations">
      {createLocationModalOpen && <CreateLocation cancel={() => setCreateLocationModalOpen(false)} />}
      <FiltersWrapper>
        <InputFilter
          value={nameFilter}
          onChange={setNameFilter}
          placeholder={"name"}
        />
        <SelectFilter
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { optionLabel: t("common:options.active"), optionValue: "true" },
            { optionLabel: t("common:options.inactive"), optionValue: "false" },
          ]}
        />
        <Button variant="positive" labelKey="create" onClick={() => setCreateLocationModalOpen(true)} />
      </FiltersWrapper>
      <CursorPagination
        hasNext={results.hasNext}
        hasPrevious={results.hasPrevious}
        currentPage={results.currentPage}
        fetchNextPage={results.fetchNextPage}
        fetchPreviousPage={results.fetchPreviousPage}
      />
      <DataTable
        columns={columns}
        data={results.results}
        getRowKey={(row) => row.id}
        isLoading={results.isLoading}
        onRowClick={(row) => navigate(`/locations/${row.id}`)}
      />
    </PageLayout>
  );
};

export default LocationsRoute;
